/* eslint-disable ember/no-computed-properties-in-native-classes */
import DynamicFieldsModel from '@linc-technologies/linced-common/models/dynamic-field/base-model';
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { isEmpty } from '@ember/utils';
import { cached } from '@glimmer/tracking';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr, belongsTo, type AsyncBelongsTo } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { dynamicField } from '@linc-technologies/linced-common/utils/dynamic-fields';
import moment from 'moment';
import type Contact from 'linced-app/models/enrolments/contact';
import type MedicalCondition from '@linc-technologies/linced-common/models/enrolments/medical-condition';
import type Enrolment from 'linced-app/models/people/enrolment';

// Proto: enrolments.Application.
export default class Application extends DynamicFieldsModel {
  declare [Type]: 'enrolments/application';

  @attr('number')
  declare createTime: number | undefined;

  @computed('createTime')
  get createDate() {
    return this.createTime
      ? moment.unix(this.createTime).format('YYYY-MM-DD')
      : '';
  }

  @attr('number')
  declare updateTime: number | undefined;

  // status specifies the processing status of the enrolment.
  @attr('number', { defaultValue: 1 })
  declare status: number;

  // schoolId filters Enrolment Applications based on the school's Hero Tenant ID.
  // For example, "61f78993-90a9-4392-92dd-83a4368528b8".
  @attr('string', { defaultValue: '' })
  declare schoolId: string;

  // campusId filters Enrolment Applications based on the school's Hero Campus ID.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // countryCode is a ISO Alpha 2 country code as to which country the school resides.
  @attr('string', { defaultValue: '' })
  declare countryCode: string;

  // schoolCode is a school identifier as given by a state based educational body.
  @attr('string', { defaultValue: '' })
  declare schoolCode: string;

  // enrolmentYear is the year in which the application was handed in for.
  @attr('number', { defaultValue: 0 })
  declare enrolmentYear: number;

  // authorId contains the id of the person who authored the received enrolment application.
  @attr('string', { defaultValue: '' })
  declare authorId: string;

  // personId is the linked Hero person ID.
  // This is NOT a relationship, as we need to be able to set it without a Person record if the learner is already
  // in Hero (but not currently associated with this school).
  @attr('string', { defaultValue: '' })
  declare personId: string;

  // jurisdiction is the unique ID of the applicable educational jurisdictional authority.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // jurisdictionalId is the learner's unique jurisdictional identifier, if known.
  @attr('string', { defaultValue: '' })
  declare jurisdictionalId: string;

  // contacts is the list of contacts (primary + emergency) associated with this application to enrol.
  @fragmentArray('enrolments/contact')
  declare contacts: FragmentArray<Contact>;

  // medicalContacts is the list of doctors/specialists associated with this application to enrol.
  @fragmentArray('enrolments/contact')
  declare medicalContacts: FragmentArray<Contact>;

  // medicalConditions is the list of medical conditions associated with this application to enrol.
  @fragmentArray('enrolments/medical-condition')
  declare medicalConditions: FragmentArray<MedicalCondition>;

  @dynamicField('core:dob', DataType.STRING)
  declare dob: string | undefined;

  @dynamicField('nzMOE:firstSchoolingDate', DataType.STRING)
  declare firstSchooling: string | undefined;

  @dependentKeyCompat
  @cached
  get currentYearLevel() {
    let field = 'core:currentYearLevel';
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        field = 'nzMOE:currentYearLevel';
        break;
    }
    return this.fieldValue(field);
  }

  @dependentKeyCompat
  @cached
  get intendedStart() {
    let field = 'core:intendedStart';
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        field = 'corePrimary:intendedStart';
        break;
    }
    return this.fieldValue(field);
  }

  @dependentKeyCompat
  @cached
  get gender() {
    const defaultField = 'core:genderLegal';
    let field = defaultField;
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        field = 'nzMOE:gender';
        break;
    }
    let gender = this.fieldValue(field);
    if (isEmpty(gender) && field !== defaultField) {
      gender = this.fieldValue(defaultField);
    }
    return isEmpty(gender) ? 'N' : gender;
  }

  @dependentKeyCompat
  @cached
  get firstName() {
    const legal = this.fieldValue('core:firstNameLegal');
    const preferred = this.fieldValue('core:firstNamePreferred');

    return isEmpty(preferred) ? legal : preferred;
  }

  @dependentKeyCompat
  @cached
  get lastName() {
    const legal = this.fieldValue('core:lastNameLegal');
    const preferred = this.fieldValue('core:lastNamePreferred');

    return isEmpty(preferred) ? legal : preferred;
  }

  @belongsTo('people/enrolment', { async: true, inverse: 'applicationId' })
  declare enrolment: AsyncBelongsTo<Enrolment>;
}
